import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import api from '../../api/axios'
import { API_ENDPOINTS } from '../../api/endpoints'
import {
  MaintenanceRequest,
  CreateMaintenanceRequest,
  UpdateMaintenanceRequest,
  MaintenanceFilters,
  PropertyManager,
} from '../../types/maintenance'
import { MaintenanceFilterState } from '../../types/maintenanceDetail'
import { buildMaintenanceFilters } from './utils/buildMaintenanceFilters'

/**
 * Redux state for the maintenance feature slice.
 *
 * Manages maintenance request lists, detail views, filters, pagination,
 * and dashboard statistics across all tenant environments.
 *
 * @remarks
 * Backend statuses: 0=New, 1=Received, 2=Started (auto-sets StartedAt), 3=Done (auto-sets FinishedAt+StartedAt).
 * Dashboard stats exclude status 3 (Done). Default list filter excludes done items.
 * Multi-tenant: `env: null` triggers aggregation across all user environments via `ProxyHandlerWithAggregation`.
 */
interface MaintenanceState {
  requests: MaintenanceRequest[]
  selectedRequest: MaintenanceRequest | null
  /** Whether the current user is an admin for the selected request (from backend `master` field). */
  isAdminForRequest: boolean
  loading: boolean
  error: string | null
  filters: MaintenanceFilters
  activeFilters: MaintenanceFilterState
  total: number
  page: number
  pageSize: number
  stats: {
    total: number
    new: number
    received: number
    inProgress: number
  } | null
  statsLoading: boolean
  propertyManagers: PropertyManager[]
  propertyManagersLoading: boolean
}

const initialState: MaintenanceState = {
  requests: [],
  selectedRequest: null,
  isAdminForRequest: false,
  loading: false,
  error: null,
  filters: {},
  activeFilters: {
    searchString: '',
    searchField: 'public_id',
    statusFilter: -1,
    priorityFilter: false,
  },
  total: 0,
  page: 1,
  pageSize: 20,
  stats: null,
  statsLoading: false,
  propertyManagers: [],
  propertyManagersLoading: false,
}

// Async Thunks

/**
 * Fetches maintenance requests with the given filter criteria applied.
 *
 * Builds filter array from UI state and sends to the aggregation endpoint.
 * Uses `env: null` to query across all tenant environments concurrently.
 *
 * @remarks
 * Backend: `POST /serviceman/maintenancerequests` with optional `{ filter: MaintenanceFilter[][] }` body.
 * Response: `{ result: MaintenanceRequest[] }` with `__source_env` injected per item.
 * If no filters are active, sends an empty body and relies on the backend's all-open default.
 *
 * @param filterState - Current filter state from `MaintenanceFilterState`
 * @returns Object with `requests`, `total`, `page`, `pageSize`, and `filters`
 * @throws Rejects with Finnish error message `'Suodatus epäonnistui'` on API failure
 */
export const filterMaintenanceRequests = createAsyncThunk(
  'maintenance/filter',
  async (filterState: MaintenanceFilterState, { rejectWithValue }) => {
    try {
      const filterArray = buildMaintenanceFilters(filterState)

      // If no filters, send an empty body (backend defaults to all open)
      // If filters exist, send filter object
      const requestBody = filterArray.length > 0 ? { filter: filterArray } : undefined

      const response = await api({
        method: 'POST',
        url: API_ENDPOINTS.MAINTENANCE_LIST,
        data: requestBody ? JSON.stringify(requestBody) : undefined,
        env: null, // No environment = query all companies
      } as any)

      const serverResults = response.data.result || []

      // Post-filter by specific maintenance type ID client-side.
      // Hashed string IDs can't be filtered server-side against the uint64 DB column,
      // but they match directly between request.maintenanceType and MaintenanceType.id.
      let results = serverResults
      const mt = filterState.maintenanceType
      if (mt && !String(mt).startsWith('requestType-')) {
        results = serverResults.filter((r: any) => r.maintenanceType && String(r.maintenanceType) === String(mt))
      }

      // Post-filter by company (Kohde or Isännöintitoimisto).
      // Company IDs are hashed strings — can't be filtered server-side.
      const { companyId, propertyManagerCompanyIds } = filterState
      if (companyId) {
        results = results.filter((r: any) => String(r.companyId) === String(companyId))
      } else if (propertyManagerCompanyIds && propertyManagerCompanyIds.length > 0) {
        results = results.filter((r: any) => propertyManagerCompanyIds.includes(String(r.companyId)))
      }

      return {
        requests: results,
        total: results.length,
        page: 1,
        pageSize: results.length,
        filters: filterState,
      }
    } catch (error) {
      return rejectWithValue('Suodatus epäonnistui')
    }
  }
)

/** Fetches property managers (isännöintitoimistot) across all environments. */
export const fetchPropertyManagers = createAsyncThunk<PropertyManager[]>(
  'maintenance/fetchPropertyManagers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api({
        method: 'POST',
        url: API_ENDPOINTS.PROPERTY_MANAGERS,
        env: null,
      } as any)
      return response.data.result || []
    } catch (error) {
      return rejectWithValue('Isännöintitoimistojen haku epäonnistui')
    }
  }
)

/**
 * Fetches all maintenance requests across all tenant environments without filters.
 *
 * Uses `env: null` so the backend `ProxyHandlerWithAggregation` fans out to all
 * environments listed in the JWT `target_env` claim and merges results.
 *
 * @remarks
 * Backend: `POST /serviceman/maintenancerequests` with no request body.
 * Response: `{ result: MaintenanceRequest[] }` — each item has `__source_env` injected.
 * Sending a body when fetching all would be interpreted as a filter query.
 *
 * @returns Object with `requests`, `total`, `page`, `pageSize`
 * @throws Rejects with `'Huoltopyyntöjen haku epäonnistui'` on API failure
 */
export const fetchMaintenanceRequests = createAsyncThunk('maintenance/fetchAll', async (_, { rejectWithValue }) => {
  try {
    // Note: Backend expects NO request body for fetching all requests
    // Only send body when applying filters (see original _legacy/maintenance.js line 163)
    const response = await api({
      method: 'POST',
      url: API_ENDPOINTS.MAINTENANCE_LIST,
      env: null, // No environment = query all companies
    } as any)
    // Backend returns: { result: MaintenanceRequest[], __source_env: ... }
    return {
      requests: response.data.result || [],
      total: response.data.result?.length || 0,
      page: 1,
      pageSize: response.data.result?.length || 0,
    }
  } catch (error) {
    return rejectWithValue('Huoltopyyntöjen haku epäonnistui')
  }
})

/**
 * Fetches dashboard statistics — maintenance request counts grouped by status.
 *
 * Returns aggregated counts (total, new, received, inProgress) across all
 * tenant environments. Does NOT apply search/filter parameters.
 *
 * @remarks
 * Backend: `POST /serviceman/maintenancerequests/stats` with empty body.
 * Response: `{ result: Array<{total, new, received, inProgress}> }` — one entry per environment.
 * Frontend sums all environment entries into a single stats object.
 * "Done" (status 3) items are excluded from all counts by the backend.
 *
 * @returns Aggregated `{ total, new, received, inProgress }` across all environments
 * @throws Rejects with `'Tilastojen haku epäonnistui'` on API failure
 */
export const fetchMaintenanceStats = createAsyncThunk('maintenance/fetchStats', async (_, { rejectWithValue }) => {
  try {
    const response = await api.post(API_ENDPOINTS.MAINTENANCE_STATS, {}, { env: null } as any)
    // Backend returns aggregated: { result: [{total, new, received, inProgress}, ...] }
    // Sum up stats from all environments
    const stats = response.data.result || []
    const aggregated = stats.reduce(
      (acc: any, curr: any) => ({
        total: (acc.total || 0) + (curr.total || 0),
        new: (acc.new || 0) + (curr.new || 0),
        received: (acc.received || 0) + (curr.received || 0),
        inProgress: (acc.inProgress || 0) + (curr.inProgress || 0),
      }),
      { total: 0, new: 0, received: 0, inProgress: 0 }
    )
    return aggregated
  } catch (error) {
    return rejectWithValue('Tilastojen haku epäonnistui')
  }
})

/**
 * Fetches a single maintenance request by ID.
 *
 * Requires an explicit `env` parameter to route to the correct tenant backend.
 * If `env` is omitted, the axios interceptor falls back to `localStorage.last_env`.
 *
 * @remarks
 * Backend: `GET /serviceman/maintenancerequest/:id` via `ProxyHandler` (single-env).
 * Response: `{ result: MaintenanceRequest }` with `__source_env`.
 *
 * @param params - Object with `id` (request ID) and optional `env` (tenant environment)
 * @returns The full `MaintenanceRequest` object from `response.data.result`
 * @throws Rejects with `'Huoltopyynnön haku epäonnistui'` on API failure
 */
export const fetchMaintenanceRequest = createAsyncThunk(
  'maintenance/fetchOne',
  async ({ id, env }: { id: string; env?: string | null }, { rejectWithValue }) => {
    try {
      const response = await api.get<{ result: MaintenanceRequest; master?: boolean }>(API_ENDPOINTS.MAINTENANCE_DETAIL(id), {
        env: env !== undefined ? env : undefined,
      } as any)
      return { request: response.data.result, master: response.data.master ?? false }
    } catch (error) {
      return rejectWithValue('Huoltopyynnön haku epäonnistui')
    }
  }
)

/**
 * Creates a new maintenance request with optional file attachments.
 *
 * Resolves the target environment through a priority chain:
 * 1. Explicitly passed `data.env`
 * 2. Currently selected company's `__source_env`
 * 3. Matching company from the loaded companies list
 *
 * @remarks
 * Backend: `POST /serviceman/maintenancerequest` with `multipart/form-data`.
 * Images are appended as multiple `images` fields in the FormData.
 * The environment resolution is critical for multi-tenant routing — creating
 * a request in the wrong env would associate it with the wrong backend.
 *
 * @param data - `CreateMaintenanceRequest` with title, description, priority, companyId, optional apartmentId and images
 * @returns The created `MaintenanceRequest` from `response.data`
 * @throws Rejects with `'Huoltopyyntön luonti epäonnistui'` on API failure
 */
export const createMaintenanceRequest = createAsyncThunk(
  'maintenance/create',
  async (data: CreateMaintenanceRequest, { rejectWithValue, getState }) => {
    try {
      // Resolve environment: explicitly passed > selected company > found in company list
      let env = data.env
      if (!env) {
        const state = getState() as RootState
        const { selectedCompany, companies } = state.company

        // Check if creating for selected company
        if (selectedCompany && String(selectedCompany.id) === String(data.companyId)) {
          env = String(selectedCompany.__source_env)
        } else {
          // Look up company in loaded list
          const targetCompany = companies.find(c => String(c.id || c.companyId) === String(data.companyId))
          if (targetCompany?.__source_env) {
            env = String(targetCompany.__source_env)
          }
        }
      }

      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('description', data.description)
      formData.append('priority', data.priority.toString())
      formData.append('companyId', data.companyId.toString())

      if (data.apartmentId) {
        formData.append('apartmentId', data.apartmentId.toString())
      }

      if (data.images && data.images.length > 0) {
        data.images.forEach(image => {
          formData.append('images', image)
        })
      }

      const response = await api.post<MaintenanceRequest>(API_ENDPOINTS.MAINTENANCE_CREATE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        env: env,
      } as any)

      return response.data
    } catch (error) {
      return rejectWithValue('Huoltopyynnön luonti epäonnistui')
    }
  }
)

/**
 * Updates an existing maintenance request.
 *
 * Resolves the environment using the same priority chain as create:
 * explicit env → selectedRequest.__source_env → loaded requests list lookup.
 *
 * @remarks
 * Backend: `PUT /serviceman/maintenancerequest/:id` via `ProxyHandler`.
 * The entire request object is sent (not a partial update) — matching legacy behavior.
 * Status transitions (e.g. Started → Done) auto-set `StartedAt`/`FinishedAt` on the backend.
 *
 * @param data - `UpdateMaintenanceRequest` with at least `id` and changed fields
 * @returns The updated `MaintenanceRequest` from `response.data`
 * @throws Rejects with `'Huoltopyyntön päivitys epäonnistui'` on API failure
 */
export const updateMaintenanceRequest = createAsyncThunk(
  'maintenance/update',
  async (data: UpdateMaintenanceRequest, { rejectWithValue, getState }) => {
    try {
      // Resolve environment
      let env = data.env
      if (!env) {
        const state = getState() as RootState
        const { selectedRequest, requests } = state.maintenance

        // Check selected request
        if (selectedRequest && String(selectedRequest.id) === String(data.id)) {
          env = selectedRequest.__source_env
        } else {
          // Look up in loaded requests
          const targetRequest = requests.find(r => String(r.id) === String(data.id))
          if (targetRequest?.__source_env) {
            env = targetRequest.__source_env
          }
        }
      }

      const response = await api.put<MaintenanceRequest>(API_ENDPOINTS.MAINTENANCE_UPDATE(String(data.id)), data, {
        env: env,
      } as any)
      return response.data
    } catch (error) {
      return rejectWithValue('Huoltopyynnön päivitys epäonnistui')
    }
  }
)

/**
 * Deletes a maintenance request by ID.
 *
 * @remarks
 * Backend: `DELETE /serviceman/maintenancerequest/:id`.
 * On success, removes the request from the Redux store list and clears
 * `selectedRequest` if it matches the deleted ID.
 *
 * @param id - The maintenance request ID to delete
 * @returns The deleted request ID string
 * @throws Rejects with `'Huoltopyyntön poisto epäonnistui'` on API failure
 */
export const deleteMaintenanceRequest = createAsyncThunk('maintenance/delete', async (id: string, { rejectWithValue }) => {
  try {
    await api.delete(API_ENDPOINTS.MAINTENANCE_DELETE(id))
    return id
  } catch (error) {
    return rejectWithValue('Huoltopyynnön poisto epäonnistui')
  }
})

// Slice
const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<MaintenanceFilters>) => {
      state.filters = action.payload
    },
    setActiveFilters: (state, action: PayloadAction<MaintenanceFilterState>) => {
      state.activeFilters = action.payload
    },
    clearFilters: state => {
      state.filters = {}
      state.activeFilters = {
        searchString: '',
        searchField: 'request_description',
        statusFilter: -1,
        priorityFilter: false,
        maintenanceType: undefined,
        propertyManagerGuid: undefined,
        propertyManagerCompanyIds: undefined,
        companyId: undefined,
      }
    },
    setSelectedRequest: (state, action: PayloadAction<MaintenanceRequest | null>) => {
      state.selectedRequest = action.payload
      state.isAdminForRequest = false
    },
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    // Filter maintenance requests
    builder
      .addCase(filterMaintenanceRequests.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(filterMaintenanceRequests.fulfilled, (state, action) => {
        state.loading = false
        state.requests = action.payload.requests
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
      })
      .addCase(filterMaintenanceRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch all
    builder
      .addCase(fetchMaintenanceRequests.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMaintenanceRequests.fulfilled, (state, action) => {
        state.loading = false
        state.requests = action.payload.requests
        state.total = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
      })
      .addCase(fetchMaintenanceRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch stats (dashboard)
    builder
      .addCase(fetchMaintenanceStats.pending, state => {
        state.statsLoading = true
      })
      .addCase(fetchMaintenanceStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.stats = action.payload
      })
      .addCase(fetchMaintenanceStats.rejected, (state, action) => {
        state.statsLoading = false
        state.error = action.payload as string
      })

    // Fetch property managers
    builder
      .addCase(fetchPropertyManagers.pending, state => {
        state.propertyManagersLoading = true
      })
      .addCase(fetchPropertyManagers.fulfilled, (state, action) => {
        state.propertyManagersLoading = false
        state.propertyManagers = action.payload
      })
      .addCase(fetchPropertyManagers.rejected, state => {
        state.propertyManagersLoading = false
      })

    // Fetch one
    builder
      .addCase(fetchMaintenanceRequest.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false
        state.selectedRequest = action.payload.request
        state.isAdminForRequest = action.payload.master
      })
      .addCase(fetchMaintenanceRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.isAdminForRequest = false
      })

    // Create
    builder
      .addCase(createMaintenanceRequest.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false
        state.requests.unshift(action.payload)
        state.total += 1
      })
      .addCase(createMaintenanceRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update
    builder
      .addCase(updateMaintenanceRequest.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false
        const index = state.requests.findIndex(r => r.id === action.payload.id)
        if (index !== -1) {
          state.requests[index] = action.payload
        }
        if (state.selectedRequest?.id === action.payload.id) {
          state.selectedRequest = action.payload
        }
      })
      .addCase(updateMaintenanceRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete
    builder
      .addCase(deleteMaintenanceRequest.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false
        state.requests = state.requests.filter(r => String(r.id) !== action.payload)
        state.total -= 1
        if (String(state.selectedRequest?.id) === action.payload) {
          state.selectedRequest = null
          state.isAdminForRequest = false
        }
      })
      .addCase(deleteMaintenanceRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

// Actions
export const { setFilters, setActiveFilters, clearFilters, setSelectedRequest, clearError } = maintenanceSlice.actions

// Selectors
/** Selects the entire maintenance slice state. */
export const selectMaintenance = (state: RootState) => state.maintenance
/** Selects the maintenance request list (post-filter/aggregation). */
export const selectMaintenanceRequests = (state: RootState) => state.maintenance.requests
/** @deprecated Use {@link selectSelectedRequest} instead. */
export const selectCurrentRequest = (state: RootState) => state.maintenance.selectedRequest
/** Selects the currently viewed maintenance request detail. */
export const selectSelectedRequest = (state: RootState) => state.maintenance.selectedRequest
/** Selects the loading state for list/detail operations. */
export const selectMaintenanceLoading = (state: RootState) => state.maintenance.loading
/** Selects the current error message (Finnish), or null. */
export const selectMaintenanceError = (state: RootState) => state.maintenance.error
/** Selects the current filter configuration object. */
export const selectMaintenanceFilters = (state: RootState) => state.maintenance.filters
/** Selects the active UI filter state (search, status, priority). */
export const selectActiveFilters = (state: RootState) => state.maintenance.activeFilters
/** Selects the total count of filtered maintenance requests. */
export const selectMaintenanceTotal = (state: RootState) => state.maintenance.total
/** Selects property managers list. */
export const selectPropertyManagers = (state: RootState) => state.maintenance.propertyManagers
/** Selects property managers loading state. */
export const selectPropertyManagersLoading = (state: RootState) => state.maintenance.propertyManagersLoading
/** Selects whether the current user is an admin for the selected maintenance request. */
export const selectIsAdminForRequest = (state: RootState) => state.maintenance.isAdminForRequest

// Reducer
export default maintenanceSlice.reducer
